// stores/bondCharacteristicStore.js
import { create } from 'zustand';
import { BOND_TYPES } from '../Constants/AppConstants';

export const useBondCharacteristicStore = create((set, get) => ({
  bondCharacteristicData: {
    side: 'BUY',
    account: '',
    bondType: BOND_TYPES.CORPORATES,
    comments: '',
    duration: '',
    couponMin: '',
    couponMax: '',
    maturityMin: '',
    maturityMax: '',
    minimumRating: '',
    minimumRatingMoody: '', // Add Moody's rating field
    zeroCoupon: '',
    callable: '',
    state: '',
    underlyingRating: '',
    insured: '',
    taxable: '',
    paymentFrequency: '',
    incomeSource: '',
    altMinTax: '',
    securityCharge: ["ALL"],
    sector: '',
    nextCallDate: '',
    priceMin: '',
    priceMax: '',
    yieldMin: '',
    yieldMax: '',
    characteristicId: '',
    issuer: '', // Add issuer field
    durationComparison: "", // <-- added
  },
  expanded: {},
  // Add checked state to manage checkboxes globally.
  checked: {},
  characteristicId: null,
  // Ladder context of the characteristic currently being acted on (roll-over flow).
  // Null for characteristics that are not linked to a bond ladder.
  bondLadderId: null,
  bondLadderRungId: null,
  fieldErrors: {},
  validationErrors: {}, // Add validation errors for characteristics

  accountIdentifiers: [], // Add this to store account identifiers

  setBondCharacteristicData: (data) =>
    set((state) => ({
      bondCharacteristicData: { ...state.bondCharacteristicData, ...data },
    })),

  setExpanded: (panel, isExpanded) =>
    set((state) => ({
      expanded: { ...state.expanded, [panel]: isExpanded },
    })),

  // New setter for check state: updates the checked object based on panel and field.
  setChecked: (panel, field, isChecked) =>
    set((state) => ({
      checked: {
        ...state.checked,
        [panel]: { ...state.checked[panel], [field]: isChecked },
      },
    })),

  removeFilter: (field) =>
    set((state) => {
      const newData = { ...state.bondCharacteristicData };
      if (field === 'couponRange') {
        newData.couponMin = '';
        newData.couponMax = '';
      } else if (field === 'maturityRanges') {
        newData.maturityMin = '';
        newData.maturityMax = '';
      } else if (field === 'price') {
        newData.priceMin = '';
        newData.priceMax = '';
      } else if (field === 'yield') {
        newData.yieldMin = '';
        newData.yieldMax = '';
      } else if (field === 'securityCharge') {
        newData.securityCharge = [];
      } else {
        newData[field] = '';
      }
      return { bondCharacteristicData: newData };
    }),

  // New reset function to clear data
  resetBondCharacteristicData: () =>
    set({
      bondCharacteristicData: {
        side: 'BUY',
        bondType: BOND_TYPES.CORPORATES,
        comments: '',
        couponMin: '',
        couponMax: '',
        maturityMin: '',
        maturityMax: '',
        minimumRating: '',
        minimumRatingMoody: '', // Add Moody's rating field
        zeroCoupon: '',
        callable: '',
        duration: '',
        state: '',
        underlyingRating: '',
        insured: '',
        taxable: '',
        paymentFrequency: '',
        incomeSource: '',
        altMinTax: '',
        securityCharge: ["ALL"],
        sector: '',
        nextCallDate: '',
        priceMin: '',
        priceMax: '',
        yieldMin: '',
        yieldMax: '',
        characteristicId: '',
        issuer: '', // Add issuer field
        durationComparison: "", // <-- added
      },
      fieldErrors: {
        account: false,
        bondType: false,
        qty: false,
        side: false,
      },
      validationErrors: {}, // Reset validation errors
      expanded: {},
      checked: {},
      accountIdentifiers: [], // Add this to store account identifiers
    }),

  resetBondCharacteristicId: () =>
    set({
      characteristicId: null,
      bondLadderId: null,
      bondLadderRungId: null,
    }),

  setCurrentCharacteristicID: (id) => set({ characteristicId: id }),

  // Ladder/rung the current characteristic belongs to. Accepts the raw
  // characteristic row and normalizes missing ids to null.
  setCharacteristicLadderContext: (data) =>
    set({
      bondLadderId: data?.bondLadderId ?? null,
      bondLadderRungId: data?.bondLadderRungId ?? null,
    }),

  setFieldErrors: (errors) => set({ fieldErrors: errors }),
  clearFieldErrors: () => set({ fieldErrors: {} }),
  clearFieldError: (fieldName) => set(state => ({
    fieldErrors: {
      ...state.fieldErrors,
      [fieldName]: false
    }
  })),
  setValidationErrors: (errors) => set({ validationErrors: errors }),
  clearValidationErrors: () => set({ validationErrors: {} }),
  setValidationError: (fieldName, hasError) => set(state => ({
    validationErrors: {
      ...state.validationErrors,
      [fieldName]: hasError
    }
  })),

  fetchAccounts: async (token) => {
    try {
      const { fetchAccounts } = await import('../http/api');
      const response = await fetchAccounts(token);
      const identifiers = response.map(acc => acc.accountIdentifier);
      set({ accountIdentifiers: identifiers });

      const currentData = get().bondCharacteristicData;
      if (identifiers.length > 0 && !currentData.account) {
        get().setBondCharacteristicData({ account: identifiers[0] });
        // Clear account error when auto-filling
        get().clearFieldError('account');
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      set({ accountIdentifiers: [] });
    }
  },
}));
